import { useLanguage } from "@/contexts/LanguageContext";
import { cn, formatUSD } from "@/lib/utils";
import type { TeamBill } from "@/lib/types";

// Six-figure bill summary waterfall, shared by the admin bill list
// (AdminBills) and the team's read-only bill view (TeamManagement).
// Field order and copy follow
// docs/superpowers/specs/2026-07-06-billing-v2-bill-mockup.html:
//   原价总额 -> 折扣减免 -> 折后总消耗 -> 代金券抵扣 -> 现金抵扣 -> 应付金额
// The discount rate is intentionally not shown here (see summaryNoteDiscount).
export default function BillSummary({ bill }: { bill: TeamBill }) {
  const { t } = useLanguage();

  const Row = ({
    label,
    value,
    negative,
    emphasis,
  }: {
    label: string;
    value: string;
    negative?: boolean;
    emphasis?: boolean;
  }) => (
    <div
      className={cn(
        "flex items-center justify-between py-1.5",
        emphasis
          ? "text-sm font-semibold text-white pt-2.5 mt-1 border-t border-white/15"
          : "text-xs text-slate-300",
      )}
    >
      <span>{label}</span>
      <span className={cn("font-mono", negative && "text-emerald-400")}>
        {negative ? "-" : ""}
        {value}
      </span>
    </div>
  );

  return (
    <div className="max-w-[380px] ml-auto space-y-0">
      <Row label={t("admin.bills.summaryTotalList")} value={formatUSD(bill.total_list_amount)} />
      <Row label={t("admin.bills.summaryDiscount")} value={formatUSD(bill.discount_total)} negative />
      <Row label={t("admin.bills.summaryConsumption")} value={formatUSD(bill.total_consumption)} />
      <Row label={t("admin.bills.summaryVoucherOffset")} value={formatUSD(bill.voucher_offset)} negative />
      <Row label={t("admin.bills.summaryCashOffset")} value={formatUSD(bill.cash_offset)} negative />
      <Row label={t("admin.bills.summaryAmountDue")} value={formatUSD(bill.amount_due)} emphasis />
      <p className="text-[10px] text-slate-500 leading-relaxed pt-3">{t("admin.bills.summaryNoteBasis")}</p>
      <p className="text-[10px] text-slate-500 leading-relaxed pt-1.5">{t("admin.bills.summaryNoteDiscount")}</p>
    </div>
  );
}
